import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import SyncBanner from '../../../shared/components/SyncBanner';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const fmtDate = (str) => {
  if (!str) return null;
  return new Date(str).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtCurrency = (amount) => {
  if (!amount && amount !== 0) return null;
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(amount);
};

const fmtCurrencyFull = (amount) => {
  if (!amount && amount !== 0) return '$0.00';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount);
};

const extractDomain = (email, website) => {
  if (website) {
    const clean = website.replace(/^https?:\/\//, '').replace(/\/.*$/, '').toLowerCase();
    if (clean && !clean.includes('localhost')) return clean;
  }
  if (email) {
    const d = email.split('@')[1];
    if (d && !d.includes('example.') && !d.includes('noemail')) return d.toLowerCase();
  }
  return null;
};

const formatLocation = (city, state) => {
  if (city && state) return `${city}, ${state}`;
  return city || state || null;
};

const GlobeIcon = () => (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
    <circle cx="12" cy="12" r="10" /><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z" />
  </svg>
);

const FolderIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
    <path d="M2 4.5A1.5 1.5 0 013.5 3h3l2 2h4A1.5 1.5 0 0114 6.5v5a1.5 1.5 0 01-1.5 1.5h-9A1.5 1.5 0 012 11.5z" />
  </svg>
);

const FORM_FIELDS = [
  { key: 'display_name', label: 'Display Name', required: true, colSpan: 2 },
  { key: 'company_name', label: 'Company Name' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'website', label: 'Website' },
  { key: 'address_line1', label: 'Address', colSpan: 2 },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'zip', label: 'ZIP' },
  { key: 'country', label: 'Country' },
  { key: 'vendor_type', label: 'Type' },
  { key: 'payment_terms', label: 'Payment Terms' },
  { key: 'account_number', label: 'Account Number' },
  { key: 'tax_id', label: 'Tax ID' },
  { key: 'notes', label: 'Notes', colSpan: 2, multiline: true },
];

const emptyForm = () => {
  const f = {};
  FORM_FIELDS.forEach((field) => { f[field.key] = ''; });
  return f;
};

// ---------------------------------------------------------------------------
// Add / Edit Modal
// ---------------------------------------------------------------------------

const VendorFormModal = ({ vendor, onSave, onClose, saving }) => {
  const isEdit = !!vendor?.id;
  const [form, setForm] = useState(() => {
    if (isEdit) {
      const f = {};
      FORM_FIELDS.forEach((field) => { f[field.key] = vendor[field.key] || ''; });
      return f;
    }
    return emptyForm();
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.display_name?.trim()) return;
    onSave(form);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <form onSubmit={handleSubmit} className="relative bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col">
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
          <h2 className="text-lg font-light text-zinc-900">{isEdit ? 'Edit Vendor' : 'Add Vendor'}</h2>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-600 transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
          </button>
        </div>
        <div className="px-6 py-4 overflow-y-auto flex-1">
          <div className="grid grid-cols-2 gap-x-4 gap-y-3">
            {FORM_FIELDS.map((field) => (
              <div key={field.key} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                <label className="block text-[10px] uppercase tracking-wider text-zinc-400 mb-1">
                  {field.label}{field.required && <span className="text-red-400 ml-0.5">*</span>}
                </label>
                {field.multiline ? (
                  <textarea value={form[field.key]} onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))} rows={3}
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-800 resize-none" placeholder={`Enter ${field.label.toLowerCase()}`} />
                ) : (
                  <input type={field.type || 'text'} value={form[field.key]} onChange={(e) => setForm((p) => ({ ...p, [field.key]: e.target.value }))}
                    className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md focus:outline-none focus:ring-1 focus:ring-zinc-900 text-zinc-800" placeholder={`Enter ${field.label.toLowerCase()}`} />
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-zinc-600 border border-zinc-200 rounded-md hover:bg-zinc-50 transition-colors">Cancel</button>
          <button type="submit" disabled={saving || !form.display_name?.trim()} className="px-4 py-2 text-[11px] uppercase tracking-[0.15em] text-white bg-zinc-900 rounded-md hover:bg-zinc-800 transition-colors disabled:opacity-50">
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Add Vendor'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Vendor Detail Drawer
// ---------------------------------------------------------------------------

const VendorIsland = ({ vendor, stats, onClose, onEdit, onDelete, onApprove, deleting }) => {
  if (!vendor) return null;
  const s = stats || {};
  const domain = extractDomain(vendor.email, vendor.website);
  const location = formatLocation(vendor.city, vendor.state);
  const initial = (vendor.display_name || '?')[0].toUpperCase();
  const avgBill = s.bill_count ? Math.round((s.total_billed || 0) / s.bill_count) : 0;

  const contactItems = [
    vendor.email && { label: 'Email', value: vendor.email, link: `mailto:${vendor.email}` },
    vendor.phone && { label: 'Phone', value: vendor.phone },
    vendor.website && { label: 'Website', value: domain || vendor.website, link: vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}` },
  ].filter(Boolean);

  const addressParts = [vendor.address_line1, vendor.address_line2].filter(Boolean);
  const locationLine = [vendor.city, vendor.state, vendor.zip].filter(Boolean).join(', ');
  const hasAddress = addressParts.length > 0 || locationLine;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/20 backdrop-blur-[2px]" onClick={onClose} />

      <div
        className="relative w-full max-w-[460px] bg-white rounded-lg border border-zinc-200 shadow-xl overflow-hidden"
        style={{ animation: 'islandIn 0.25s cubic-bezier(0.16,1,0.3,1)' }}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-zinc-100">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center text-[14px] font-semibold text-zinc-600 shrink-0">
              {initial}
            </div>
            <div className="flex-1 min-w-0 pt-0.5">
              <h2 className="text-[16px] font-semibold text-zinc-900 truncate">{vendor.display_name}</h2>
              <div className="flex items-center gap-1.5 mt-0.5">
                {vendor.company_name && vendor.company_name !== vendor.display_name && (
                  <span className="text-[12px] text-zinc-400 truncate">{vendor.company_name}</span>
                )}
                {domain && (
                  <span className="text-[11px] text-zinc-400 truncate">{vendor.company_name && vendor.company_name !== vendor.display_name ? '· ' : ''}{domain}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onEdit(vendor)} className="w-7 h-7 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-zinc-700 transition-all" title="Edit">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
              </button>
              <button onClick={() => onDelete(vendor.id)} disabled={deleting} className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-zinc-400 hover:text-red-500 transition-all disabled:opacity-40" title="Delete">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
              </button>
              <button onClick={onClose} className="w-7 h-7 rounded-md hover:bg-zinc-100 flex items-center justify-center text-zinc-400 hover:text-zinc-700 transition-all">
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
              </button>
            </div>
          </div>

          {/* Needs review banner */}
          {vendor.needs_review && (
            <div className="mt-3 flex items-center gap-3 px-3 py-2.5 bg-amber-50 border border-amber-200 rounded-md">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-amber-500 shrink-0">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" />
              </svg>
              <span className="text-[12px] text-amber-700 flex-1">New vendor — approve to keep in your vendor list</span>
              <button
                onClick={() => onApprove?.(vendor.id)}
                className="px-3 py-1 bg-zinc-900 text-white text-[11px] font-medium rounded-md hover:bg-zinc-800 transition-colors shrink-0"
              >
                Approve
              </button>
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="px-5 py-3 border-b border-zinc-100 grid grid-cols-3 gap-4">
          <div>
            <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-0.5">Total Billed</p>
            <p className="text-[16px] font-semibold text-zinc-900 tabular-nums">{fmtCurrencyFull(s.total_billed || 0)}</p>
          </div>
          <div>
            <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-0.5">Bills</p>
            <p className="text-[16px] font-semibold text-zinc-900 tabular-nums">{s.bill_count || 0}</p>
          </div>
          {avgBill > 0 && (
            <div>
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-0.5">Avg Bill</p>
              <p className="text-[16px] font-semibold text-zinc-900 tabular-nums">{fmtCurrency(avgBill)}</p>
            </div>
          )}
        </div>

        {/* Details */}
        <div className="max-h-[260px] overflow-y-auto">
          {(contactItems.length > 0 || hasAddress) && (
            <div className="px-5 py-3 border-b border-zinc-100 grid grid-cols-2 gap-4">
              {contactItems.length > 0 && (
                <div className="space-y-2">
                  {contactItems.map((item, i) => (
                    <div key={i}>
                      <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium">{item.label}</p>
                      {item.link ? (
                        <a href={item.link} target="_blank" rel="noopener noreferrer" className="text-[12px] text-blue-600 hover:text-blue-700 block truncate">{item.value}</a>
                      ) : (
                        <p className="text-[12px] text-zinc-700 truncate">{item.value}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {hasAddress && (
                <div>
                  <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-1">Address</p>
                  <div className="text-[12px] text-zinc-600 leading-relaxed">
                    {addressParts.map((line, i) => <div key={i}>{line}</div>)}
                    {locationLine && <div>{locationLine}</div>}
                    {vendor.country && vendor.country !== 'US' && <div>{vendor.country}</div>}
                  </div>
                </div>
              )}
            </div>
          )}

          {vendor.notes && (
            <div className="px-5 py-3 border-b border-zinc-100">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-medium mb-1">Notes</p>
              <p className="text-[12px] text-zinc-600 whitespace-pre-wrap leading-relaxed">{vendor.notes}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2.5 flex items-center justify-between bg-zinc-50">
          <span className="text-[10px] text-zinc-400">Added {fmtDate(vendor.created_at) || '—'}</span>
          <div className="flex items-center gap-2">
            {vendor.source && vendor.source !== 'upload' && vendor.source !== 'manual' && (
              <span className="text-[10px] text-zinc-400 capitalize">{vendor.source}</span>
            )}
            {vendor.vendor_type && (
              <span className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 rounded">{vendor.vendor_type}</span>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes islandIn {
          from { opacity: 0; transform: scale(0.97) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const VendorsView = ({ loaded }) => {
  const [vendors, setVendors] = useState([]);
  const [vendorStats, setVendorStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('billed');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);

  // Folder state
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [folderContextMenu, setFolderContextMenu] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [folderVendorIds, setFolderVendorIds] = useState(null);

  const addMenuRef = useRef(null);
  const folderPickerRef = useRef(null);
  const folderCtxRef = useRef(null);

  // -------------------------------------------------------------------------
  // Fetching
  // -------------------------------------------------------------------------

  const fetchFolders = async () => {
    try {
      const res = await apiFetch('/api/vendor-folders');
      const data = await res.json();
      if (res.ok) setFolders(data.folders || []);
    } catch { /* silent */ }
  };

  const fetchFolderMembers = useCallback(async (folderId) => {
    if (!folderId) { setFolderVendorIds(null); return; }
    try {
      const res = await apiFetch(`/api/vendor-folders/${folderId}/vendors`);
      const data = await res.json();
      if (res.ok) setFolderVendorIds(new Set(data.vendorIds || []));
      else setFolderVendorIds(null);
    } catch { setFolderVendorIds(null); }
  }, []);

  const fetchVendors = useCallback(async () => {
    try {
      const [vendorRes, statsRes] = await Promise.all([
        apiFetch('/api/vendors'),
        apiFetch('/api/vendors/stats'),
      ]);
      const vendorData = await vendorRes.json();
      const statsData = await statsRes.json();
      setVendors(vendorData.vendors || []);
      setVendorStats(statsData.stats || {});
    } catch {
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); fetchFolders(); }, [fetchVendors]);
  useEffect(() => { fetchFolderMembers(activeFolder); }, [activeFolder, fetchFolderMembers]);
  useEffect(() => { setSelectedIds(new Set()); }, [activeFolder]);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (addMenuRef.current && !addMenuRef.current.contains(e.target)) setShowAddMenu(false);
      if (folderPickerRef.current && !folderPickerRef.current.contains(e.target)) setShowFolderPicker(false);
      if (folderCtxRef.current && !folderCtxRef.current.contains(e.target)) setFolderContextMenu(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // -------------------------------------------------------------------------
  // Folder actions
  // -------------------------------------------------------------------------

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const res = await apiFetch('/api/vendor-folders', { method: 'POST', body: JSON.stringify({ name: newFolderName.trim() }) });
      if (res.ok) { setNewFolderName(''); setShowCreateFolder(false); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleRenameFolder = async (id) => {
    if (!renameValue.trim()) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${id}`, { method: 'PUT', body: JSON.stringify({ name: renameValue.trim() }) });
      if (res.ok) { setRenamingFolder(null); setRenameValue(''); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleDeleteFolder = async (id) => {
    try {
      const res = await apiFetch(`/api/vendor-folders/${id}`, { method: 'DELETE' });
      if (res.ok) { if (activeFolder === id) setActiveFolder(null); setFolderContextMenu(null); fetchFolders(); }
    } catch { /* silent */ }
  };

  const handleAddToFolder = async (folderId) => {
    if (selectedIds.size === 0) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${folderId}/vendors`, { method: 'POST', body: JSON.stringify({ vendorIds: [...selectedIds] }) });
      if (res.ok) { setSelectedIds(new Set()); setShowFolderPicker(false); fetchFolders(); if (activeFolder) fetchFolderMembers(activeFolder); }
    } catch { /* silent */ }
  };

  const handleRemoveFromFolder = async () => {
    if (!activeFolder || selectedIds.size === 0) return;
    try {
      const res = await apiFetch(`/api/vendor-folders/${activeFolder}/vendors`, { method: 'DELETE', body: JSON.stringify({ vendorIds: [...selectedIds] }) });
      if (res.ok) { setSelectedIds(new Set()); fetchFolders(); fetchFolderMembers(activeFolder); }
    } catch { /* silent */ }
  };

  const toggleSelect = (id, e) => {
    e.stopPropagation();
    setSelectedIds((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  // -------------------------------------------------------------------------
  // Stats
  // -------------------------------------------------------------------------

  const totalBilled = useMemo(() => Object.values(vendorStats).reduce((sum, s) => sum + (s.total_billed || 0), 0), [vendorStats]);
  const totalBills = useMemo(() => Object.values(vendorStats).reduce((sum, s) => sum + (s.bill_count || 0), 0), [vendorStats]);
  const reviewCount = useMemo(() => vendors.filter((v) => v.needs_review).length, [vendors]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'name' ? 'asc' : 'desc'); }
  };

  const filtered = useMemo(() => {
    let list = vendors;
    // Filter by folder
    if (activeFolder && folderVendorIds) {
      list = list.filter((v) => folderVendorIds.has(v.id));
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((v) =>
        (v.display_name || '').toLowerCase().includes(q) ||
        (v.company_name || '').toLowerCase().includes(q) ||
        (v.email || '').toLowerCase().includes(q) ||
        (v.city || '').toLowerCase().includes(q)
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const sa = vendorStats[a.id] || {};
      const sb = vendorStats[b.id] || {};
      switch (sortBy) {
        case 'billed': return ((sb.total_billed || 0) - (sa.total_billed || 0)) * dir;
        case 'bills': return ((sb.bill_count || 0) - (sa.bill_count || 0)) * dir;
        case 'location': return ((formatLocation(a.city, a.state) || '').localeCompare(formatLocation(b.city, b.state) || '')) * dir;
        case 'domain': return ((extractDomain(a.email, a.website) || '').localeCompare(extractDomain(b.email, b.website) || '')) * dir;
        default: return (a.display_name || '').localeCompare(b.display_name || '') * dir;
      }
    });
  }, [vendors, search, sortBy, sortDir, vendorStats, activeFolder, folderVendorIds]);

  // -------------------------------------------------------------------------
  // CRUD
  // -------------------------------------------------------------------------

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      const res = await apiFetch('/api/vendors', { method: 'POST', body: JSON.stringify(form) });
      if (res.ok) { const data = await res.json(); setVendors((prev) => [data.vendor, ...prev]); setShowForm(false); }
    } catch { setError('Failed to create vendor'); } finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    if (!editTarget?.id) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/vendors/${editTarget.id}`, { method: 'PUT', body: JSON.stringify(form) });
      if (res.ok) {
        const data = await res.json();
        setVendors((prev) => prev.map((v) => (v.id === editTarget.id ? { ...v, ...data.vendor } : v)));
        setEditTarget(null);
        if (selectedVendor?.id === editTarget.id) setSelectedVendor((prev) => ({ ...prev, ...data.vendor }));
      }
    } catch { setError('Failed to update vendor'); } finally { setSaving(false); }
  };

  const handleDelete = async (vendorId) => {
    setDeleting(true);
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}`, { method: 'DELETE' });
      if (res.ok) { setVendors((prev) => prev.filter((v) => v.id !== vendorId)); setSelectedVendor(null); }
    } catch { setError('Failed to delete vendor'); } finally { setDeleting(false); }
  };

  const handleApprove = async (vendorId) => {
    try {
      const res = await apiFetch(`/api/vendors/${vendorId}/approve`, { method: 'PUT' });
      if (res.ok) {
        setVendors((prev) => prev.map((v) => v.id === vendorId ? { ...v, needs_review: false } : v));
        if (selectedVendor?.id === vendorId) setSelectedVendor((prev) => prev ? { ...prev, needs_review: false } : prev);
      }
    } catch { /* silent */ }
  };

  const handleApproveAll = async () => {
    const reviewVendors = vendors.filter((v) => v.needs_review);
    for (const v of reviewVendors) {
      try {
        await apiFetch(`/api/vendors/${v.id}/approve`, { method: 'PUT' });
      } catch { /* continue */ }
    }
    setVendors((prev) => prev.map((v) => ({ ...v, needs_review: false })));
  };

  const handleEdit = (vendor) => { setSelectedVendor(null); setEditTarget(vendor); };

  // Group vendors by review status for sectioned display
  const needsReviewVendors = useMemo(() => filtered.filter(v => v.needs_review), [filtered]);
  const activeVendors = useMemo(() => filtered.filter(v => !v.needs_review), [filtered]);

  const SortHeader = ({ col, children, align }) => {
    const active = sortBy === col;
    return (
      <th
        onClick={() => handleSort(col)}
        className={`px-4 py-2 text-[10.5px] uppercase tracking-[0.08em] font-semibold cursor-pointer select-none hover:text-zinc-700 transition-colors whitespace-nowrap ${
          align === 'right' ? 'text-right pr-5' : 'text-left'
        } ${active ? 'text-zinc-600' : 'text-zinc-400'}`}
      >
        {children}
        {active && <span className="ml-1 text-[8px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
      </th>
    );
  };

  const renderVendorRow = (v, i) => {
    const domain = extractDomain(v.email, v.website);
    const location = formatLocation(v.city, v.state);
    const s = vendorStats[v.id] || {};
    const isSelected = selectedIds.has(v.id);
    const subtitle = v.vendor_type || (v.company_name && v.company_name !== v.display_name ? v.company_name : null) || (domain || null);

    return (
      <tr
        key={v.id}
        onClick={() => setSelectedVendor(v)}
        className={`group border-b border-zinc-100 last:border-0 transition-colors cursor-pointer ${
          isSelected ? 'bg-zinc-50' : 'hover:bg-zinc-50/50'
        }`}
        style={{ opacity: loaded ? 1 : 0, transition: `opacity 0.4s cubic-bezier(0.16,1,0.3,1) ${300 + i * 12}ms` }}
      >
        <td className="pl-4 pr-1 py-3 w-8" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => toggleSelect(v.id, e)}
            className="w-3.5 h-3.5 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900 focus:ring-offset-0 cursor-pointer"
          />
        </td>

        <td className="pl-2 pr-4 py-3">
          <div className="min-w-0">
            <span className="text-[13px] font-medium text-zinc-900 truncate block leading-snug">{v.display_name}</span>
            {subtitle && <span className="text-[11px] text-zinc-400 truncate block">{subtitle}</span>}
          </div>
        </td>

        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {v.needs_review ? (
            <button
              onClick={() => handleApprove(v.id)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors whitespace-nowrap"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
              Approve
            </button>
          ) : v.source && v.source !== 'upload' && v.source !== 'manual' ? (
            <span className="text-[12px] text-zinc-400 whitespace-nowrap capitalize">{v.source}</span>
          ) : (
            <span className="text-[12px] text-zinc-400">Manual</span>
          )}
        </td>

        <td className="px-4 py-3">
          {s.bill_count ? (
            <span className="text-[12px] text-blue-600 font-medium tabular-nums whitespace-nowrap underline decoration-blue-200 underline-offset-2">{s.bill_count} bill{s.bill_count !== 1 ? 's' : ''}</span>
          ) : null}
        </td>

        <td className="px-4 py-3">
          {location ? (
            <span className="text-[12px] text-zinc-500 whitespace-nowrap">{location}</span>
          ) : null}
        </td>

        <td className="px-4 py-3">
          {v.created_at ? (
            <span className="text-[12px] text-zinc-500 whitespace-nowrap tabular-nums">{fmtDate(v.created_at)}</span>
          ) : null}
        </td>

        <td className="px-5 py-3 text-right">
          {s.total_billed ? (
            <span className="text-[12px] text-zinc-900 font-semibold tabular-nums">{fmtCurrency(s.total_billed)}</span>
          ) : null}
        </td>
      </tr>
    );
  };

  return (
    <div>
      {/* Header */}
      <div
        className="mb-5 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '100ms' }}
      >
        <div className="flex items-center justify-between">
          <h1 className="text-[22px] font-semibold text-zinc-900">Vendor Directory</h1>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 text-[12px] font-medium text-white bg-zinc-900 rounded-md hover:bg-zinc-800 transition-colors flex items-center gap-2"
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2v8M2 6h8" /></svg>
            Add Vendor
          </button>
        </div>
      </div>

      {/* Sync banner */}
      <SyncBanner entity="vendors" label="vendors" onSynced={fetchVendors} />

      {/* Tab bar — underline style */}
      <div
        className="flex items-center border-b border-zinc-200 mb-4 relative z-30 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: '160ms' }}
      >
        <button
          onClick={() => setActiveFolder(null)}
          className={`inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium border-b-2 transition-all whitespace-nowrap -mb-px ${
            activeFolder === null
              ? 'border-zinc-900 text-zinc-900'
              : 'border-transparent text-zinc-400 hover:text-zinc-600'
          }`}
        >
          All Vendors
          <span className={`text-[11px] px-1.5 py-px rounded font-medium tabular-nums ${activeFolder === null ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
            {vendors.length}
          </span>
        </button>

        {folders.map((f) => (
          <div key={f.id} className="relative">
            {renamingFolder === f.id ? (
              <form onSubmit={(e) => { e.preventDefault(); handleRenameFolder(f.id); }} className="inline-flex items-center px-3 py-2">
                <input
                  autoFocus value={renameValue}
                  onChange={(e) => setRenameValue(e.target.value)}
                  onBlur={() => setRenamingFolder(null)}
                  onKeyDown={(e) => { if (e.key === 'Escape') setRenamingFolder(null); }}
                  className="px-2 py-1 text-[12px] border border-zinc-300 rounded w-28 outline-none focus:ring-1 focus:ring-zinc-400"
                  placeholder="Folder name"
                />
              </form>
            ) : (
              <button
                onClick={() => setActiveFolder(f.id)}
                onContextMenu={(e) => { e.preventDefault(); setFolderContextMenu(f.id); }}
                className={`inline-flex items-center gap-2 px-4 py-2 text-[13px] font-medium border-b-2 transition-all whitespace-nowrap -mb-px ${
                  activeFolder === f.id
                    ? 'border-zinc-900 text-zinc-900'
                    : 'border-transparent text-zinc-400 hover:text-zinc-600'
                }`}
              >
                {f.name}
                <span className={`text-[11px] px-1.5 py-px rounded font-medium tabular-nums ${activeFolder === f.id ? 'bg-zinc-900 text-white' : 'bg-zinc-100 text-zinc-500'}`}>
                  {f.vendor_count}
                </span>
              </button>
            )}
            {folderContextMenu === f.id && (
              <div ref={folderCtxRef} className="absolute top-full left-0 mt-1 bg-white border border-zinc-200 rounded-md shadow-lg z-30 py-1 min-w-[120px]">
                <button onClick={() => { setRenamingFolder(f.id); setRenameValue(f.name); setFolderContextMenu(null); }} className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-50">Rename</button>
                <button onClick={() => handleDeleteFolder(f.id)} className="w-full text-left px-3 py-1.5 text-[12px] text-red-600 hover:bg-red-50">Delete</button>
              </div>
            )}
          </div>
        ))}

        {/* Inline add-folder */}
        <div className="relative ml-1" ref={addMenuRef}>
          {showCreateFolder ? (
            <form onSubmit={(e) => { e.preventDefault(); handleCreateFolder(); }} className="flex items-center gap-2 px-2 py-1.5">
              <input
                autoFocus value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setShowCreateFolder(false); setNewFolderName(''); } }}
                placeholder="Folder name"
                className="px-2 py-1 text-[12px] border border-zinc-300 rounded w-28 outline-none focus:ring-1 focus:ring-zinc-400"
              />
              <button type="submit" className="px-2 py-1 text-[11px] bg-zinc-900 text-white rounded hover:bg-zinc-800 font-medium">Add</button>
              <button type="button" onClick={() => { setShowCreateFolder(false); setNewFolderName(''); }} className="text-zinc-400 hover:text-zinc-600">
                <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l8 8M12 4l-8 8" /></svg>
              </button>
            </form>
          ) : (
            <button
              onClick={() => { setShowCreateFolder(true); setShowAddMenu(false); }}
              className="inline-flex items-center px-2 py-2 text-zinc-300 hover:text-zinc-500 transition-colors -mb-px border-b-2 border-transparent"
              title="New folder"
            >
              <svg width="14" height="14" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M6 2v8M2 6h8" /></svg>
            </button>
          )}
        </div>
      </div>

      {/* Search bar + summary */}
      <div
        className="mb-3 flex items-center gap-3 transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(8px)', transitionDelay: '220ms' }}
      >
        <div className="relative flex-1 max-w-[260px]">
          <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <circle cx="7" cy="7" r="5" /><path d="M11 11L14 14" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by vendor..."
            className="w-full pl-8 pr-3 py-1.5 bg-white border border-zinc-200 text-[13px] text-zinc-900 placeholder-zinc-400 rounded-md outline-none focus:ring-1 focus:ring-zinc-300 transition-shadow"
          />
        </div>

        {!loading && totalBills > 0 && (
          <p className="ml-auto text-[12px] text-zinc-400 tabular-nums">
            {fmtCurrency(totalBilled)} across {totalBills} bill{totalBills !== 1 ? 's' : ''}
          </p>
        )}
      </div>

      {/* Selection action bar */}
      {selectedIds.size > 0 && (
        <div
          className="mb-3 flex items-center gap-2 px-3 py-2 bg-zinc-900 rounded-md text-white"
          style={{ animation: 'fadeIn 0.15s ease-out' }}
        >
          <span className="text-[12px] font-medium">{selectedIds.size} selected</span>
          <span className="text-stone-500 text-[12px]">·</span>

          <div className="relative" ref={folderPickerRef}>
            <button
              onClick={() => setShowFolderPicker(!showFolderPicker)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border border-stone-600 rounded hover:bg-white/10 transition-colors"
            >
              <FolderIcon size={10} />
              Add to folder
            </button>
            {showFolderPicker && folders.length > 0 && (
              <div className="absolute left-0 top-full mt-1 bg-white border border-zinc-200 rounded-md shadow-lg z-30 py-1 min-w-[160px]">
                {folders.map((f) => (
                  <button
                    key={f.id}
                    onClick={() => handleAddToFolder(f.id)}
                    className="w-full text-left px-3 py-1.5 text-[12px] text-zinc-700 hover:bg-zinc-50 flex items-center gap-2"
                  >
                    <FolderIcon size={11} />
                    {f.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {activeFolder && (
            <button
              onClick={handleRemoveFromFolder}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-medium border border-red-800 text-red-400 rounded hover:bg-red-900/30 transition-colors"
            >
              Remove from folder
            </button>
          )}

          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-[11px] text-stone-400 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="mb-3 px-4 py-2.5 bg-red-50 border border-red-200 rounded-md flex items-center gap-3">
          <span className="text-[13px] text-red-600">{error}</span>
          <button onClick={() => { setError(null); fetchVendors(); }} className="ml-auto text-[12px] text-red-500 hover:text-red-700 font-medium">Retry</button>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div
          className="flex items-center justify-center py-24 transition-all duration-700"
          style={{ opacity: loaded ? 1 : 0, transitionDelay: '280ms' }}
        >
          <div className="w-5 h-5 border-2 border-zinc-200 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      )}

      {/* Empty state */}
      {!loading && filtered.length === 0 && (
        <div
          className="py-16 text-center border border-zinc-200 rounded-lg bg-white transition-all duration-700"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '280ms' }}
        >
          {search || activeFolder ? (
            <div>
              <p className="text-[13px] text-zinc-400">
                {activeFolder ? 'No vendors in this folder.' : 'No vendors match your search.'}
              </p>
              {search && <button onClick={() => setSearch('')} className="mt-2 text-[12px] text-zinc-500 hover:text-zinc-700 underline">Clear search</button>}
            </div>
          ) : (
            <div>
              <p className="text-[13px] text-zinc-400 mb-3">No vendors found. Sync from QuickBooks or add them manually.</p>
              <button onClick={() => setShowForm(true)} className="px-4 py-2 text-[12px] font-medium text-white bg-zinc-900 hover:bg-zinc-800 rounded-md transition-colors">
                Add Your First Vendor
              </button>
            </div>
          )}
        </div>
      )}

      {/* Pending review — separate elevated section */}
      {!loading && needsReviewVendors.length > 0 && (
        <div
          className="mb-3 rounded-lg overflow-hidden border border-amber-200 bg-white transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: '280ms' }}
        >
          {/* Section header */}
          <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 border-b border-amber-200">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" className="shrink-0">
              <path d="M12 2L2 22h20L12 2z" fill="#f59e0b" opacity="0.2" stroke="#f59e0b" strokeWidth="1.5" strokeLinejoin="round" />
              <path d="M12 10v4" stroke="#b45309" strokeWidth="2" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1" fill="#b45309" />
            </svg>
            <span className="text-[12px] font-semibold text-amber-900">Pending review</span>
            <span className="text-[11px] font-medium text-amber-600 tabular-nums">{needsReviewVendors.length}</span>
            <button
              onClick={handleApproveAll}
              className="ml-auto text-[11px] font-medium text-amber-700 hover:text-amber-900 transition-colors"
            >
              Approve all
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 860 }}>
              <tbody>
                {needsReviewVendors.map((v, i) => renderVendorRow(v, i))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Active vendors — main table */}
      {!loading && activeVendors.length > 0 && (
        <div
          className="border border-zinc-200 rounded-lg overflow-hidden bg-white transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
          style={{ opacity: loaded ? 1 : 0, transform: loaded ? 'translateY(0)' : 'translateY(12px)', transitionDelay: needsReviewVendors.length > 0 ? '340ms' : '280ms' }}
        >
          {/* Section header */}
          {needsReviewVendors.length > 0 && (
            <div className="flex items-center gap-2 px-4 py-2 bg-zinc-50 border-b border-zinc-200">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="shrink-0">
                <circle cx="12" cy="12" r="10" fill="#10b981" opacity="0.12" stroke="#10b981" strokeWidth="1.5" />
                <path d="M8 12l3 3 5-5" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <span className="text-[12px] font-semibold text-zinc-600">Active vendors</span>
              <span className="text-[11px] font-medium text-zinc-400 tabular-nums">{activeVendors.length}</span>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full border-collapse" style={{ minWidth: 860 }}>
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/50">
                  <th className="pl-4 pr-1 py-2 w-8"></th>
                  <SortHeader col="name">Vendor</SortHeader>
                  <SortHeader col="domain">Status</SortHeader>
                  <SortHeader col="bills">Bills</SortHeader>
                  <SortHeader col="location">Location</SortHeader>
                  <th className="px-4 py-2 text-[10.5px] uppercase tracking-[0.08em] font-semibold text-left text-zinc-400 whitespace-nowrap">Added</th>
                  <SortHeader col="billed" align="right">Total Billed</SortHeader>
                </tr>
              </thead>
              <tbody>
                {activeVendors.map((v, i) => renderVendorRow(v, i + needsReviewVendors.length))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Overlays */}
      {selectedVendor && (
        <VendorIsland vendor={selectedVendor} stats={vendorStats[selectedVendor.id]} onClose={() => setSelectedVendor(null)} onEdit={handleEdit} onDelete={handleDelete} onApprove={handleApprove} deleting={deleting} />
      )}
      {showForm && <VendorFormModal onSave={handleCreate} onClose={() => setShowForm(false)} saving={saving} />}
      {editTarget && <VendorFormModal vendor={editTarget} onSave={handleUpdate} onClose={() => setEditTarget(null)} saving={saving} />}
    </div>
  );
};

export default VendorsView;
